import type { DragEvent } from 'react'

const GW_WM_DRAG_MIME = 'application/x-gw-wm-drag'

type GwWmCompetencyDragPayload = { kind: 'competency'; fromIndex: number }

type GwWmSubCompetencyDragPayload = {
  kind: 'subCompetency'
  competencyId: string
  fromIndex: number
}

type GwWmDragPayload = GwWmCompetencyDragPayload | GwWmSubCompetencyDragPayload

let activeDrag: GwWmDragPayload | null = null

/** getData is unreliable in dragover; use this for dropEffect / guards. */
export function setGwWmActiveDrag(payload: GwWmDragPayload | null) {
  activeDrag = payload
}

export function getGwWmActiveDrag(): GwWmDragPayload | null {
  return activeDrag
}

/** Plain-text carrier so drop/getData works in browsers that restrict custom MIME types. */
const GW_WM_TEXT_PREFIX = 'gw-wm-dnd:'

function parseGwWmPayloadJson(json: string): GwWmDragPayload | null {
  const o = JSON.parse(json) as GwWmDragPayload
  if (o.kind === 'competency' && typeof o.fromIndex === 'number') return o
  if (
    o.kind === 'subCompetency' &&
    typeof o.competencyId === 'string' &&
    typeof o.fromIndex === 'number'
  ) {
    return o
  }
  return null
}

export function setGwWmDragData(dt: DataTransfer, payload: GwWmDragPayload) {
  const json = JSON.stringify(payload)
  dt.setData(GW_WM_DRAG_MIME, json)
  dt.setData('text/plain', `${GW_WM_TEXT_PREFIX}${json}`)
  dt.effectAllowed = 'move'
}

export function getGwWmDragData(e: DragEvent): GwWmDragPayload | null {
  try {
    const fromMime = e.dataTransfer.getData(GW_WM_DRAG_MIME)
    if (fromMime) return parseGwWmPayloadJson(fromMime)

    const plain = e.dataTransfer.getData('text/plain')
    if (plain.startsWith(GW_WM_TEXT_PREFIX)) {
      return parseGwWmPayloadJson(plain.slice(GW_WM_TEXT_PREFIX.length))
    }
    return null
  } catch {
    return null
  }
}

/**
 * Use the full card/row as the drag image (the grip is only the drag source).
 */
export function setGwWmDragPreviewFromNode(
  dataTransfer: DataTransfer,
  clientX: number,
  clientY: number,
  previewRoot: HTMLElement | null,
): void {
  if (!previewRoot) return
  const rect = previewRoot.getBoundingClientRect()
  const clone = previewRoot.cloneNode(true) as HTMLElement
  clone.classList.add('gw-wm__drag-preview-clone')
  clone.setAttribute('aria-hidden', 'true')
  clone.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'))
  Object.assign(clone.style, {
    position: 'fixed',
    left: '-10000px',
    top: '0',
    width: `${rect.width}px`,
    boxSizing: 'border-box',
    pointerEvents: 'none',
    zIndex: '2147483647',
    margin: '0',
  })
  document.body.appendChild(clone)
  void clone.offsetWidth
  const offsetX = Math.max(0, Math.round(clientX - rect.left))
  const offsetY = Math.max(0, Math.round(clientY - rect.top))
  dataTransfer.setDragImage(clone, offsetX, offsetY)
  window.setTimeout(() => clone.remove(), 0)
}
